<template>
    <div class="container-fluid">
        <nav>
            <ol class="breadcrumb">
                <li class="breadcrumb-item"><router-link to="/mypage">マイページ</router-link></li>
                <li class="breadcrumb-item">アカウント情報</li>
            </ol>
        </nav>
        <div class="container">
            <div class="row" v-if="!isLoading">
                <div class="col-xl-3">
                    <div class="sideinfo sticky">
                        <img :src="org.profile_url + '?' + org.profile_hash" class="img-fluid img-thumbnail mx-auto d-block cover-img" v-if="org.profile_url" />
                        <div>
                            <h6 class="line">ユーザ情報</h6>
                            <div>
                                <ruby>
                                    {{org.name}}
                                    <rt>{{org.kana}}</rt>
                                </ruby>
                            </div>
                            <div class="small">
                                <div :class="{'text-danger': !org.email_verified_at, 'text-success': org.email_verified_at}">
                                    {{org.email}}
                                    <span class="badge badge-success badge-pill" v-if="org.email_verified_at" data-toggle="tooltip" title="承認済み">
                                        <i class="fas fa-check"></i>
                                    </span>
                                    <span class="badge badge-danger badge-pill" v-if="org.is_prohibited" data-toggle="tooltip" title="制限ユーザー">
                                        <i class="fas fa-user-lock"></i>
                                    </span>
                                </div>
                                <div class="text-muted small mb-2" v-if="!org.email_verified_at">
                                    <span class="badge badge-danger badge-pill">
                                        メールアドレス未承認
                                    </span>
                                    <div>
                                        メールアドレスが承認されていません。<br>
                                        未承認状態では、ドメインの取得ができません。<br>
                                        承認メールが送られていない場合、<a href="/email/verify">こちらから</a>確認メールを再送できます。
                                    </div>
                                </div>
                            </div>
                            <div>
                                <span v-if="!org.rating" class="badge badge-secondary">許可: 無し</span>
                                <span v-else class="badge" :class="['badge-' + org.rating.color]">許可: {{org.rating.rating_name}}</span>
                            </div>
                        </div>
                        <div v-if="org.subdomain">
                            <h6 class="line">本棚の情報</h6>
                            <div class="big">
                                {{org.bookshelf_name}}
                            </div>
                            <div class="small">
                                <a :href="'//' + org.subdomain + $store.state.base_domain" target="_blank">{{org.subdomain}}{{$store.state.base_domain}}</a>
                            </div>
                            <div>
                                <p class="small" v-html="$filters.url(org.profile)">
                                </p>
                            </div>
                            <div v-if="org.bluesky" class="mb-3">
                                <a :href="'https://bluesky.app/profile/' + org.bluesky" class="btn btn-primary btn-sm btn-icon" target="_blank">
                                    <img src="/img/bluesky-white.svg">
                                    @{{org.bluesky}}
                                </a>
                            </div>
                            <div v-if="org.twitter">
                                <a :href="'https://x.com/' + org.twitter" class="btn btn-dark btn-sm btn-icon" target="_blank">
                                    <i class="fab fa-twitter"></i>
                                    @{{org.twitter}}
                                </a>
                            </div>
                        </div>
                        <div>
                            <h6 class="line">セキュリティ</h6>
                            <div class="mb-2 small">
                                <div v-if="org.is_google == 1">Google連携ログイン</div>
                                <div v-if="org.has_password == 1">パスワードログイン</div>
                            </div>
                        </div>
                        <div class="line no-line" v-if="user.subdomains">
                            <h6 class="line">旧サブドメイン</h6>
                            <div>
                                <div v-for="s in user.subdomains" :key="s.id" class="monospace">
                                    {{s.subdomain}}
                                </div>
                            </div>
                        </div>
                        <div v-show="false">
                            <h6 class="line">しきい値</h6>
                            <div>
                                最大作品数: {{$filters.numberFormat(user.novel_limit)}} 作品
                            </div>
                            <div>
                                最大エピソード数: {{$filters.numberFormat(user.episode_limit)}} 話
                            </div>
                            <div>
                                最大記事数: {{$filters.numberFormat(user.article_limit)}} 記事
                            </div>
                            <div class="small text-muted">
                                これらの制限値は予告なく変更されることがあります。
                                制限に到達した場合、お問い合わせ頂ければ制限値を拡張いたします。
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-xl-9">
                    <h3 class="account-caption">
                        ユーザ基本情報
                        <button class="btn btn-light" v-if="org.subdomain && !user.subdomain" @click="load">
                            <i class="fas fa-sync-alt"></i>
                        </button>
                    </h3>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label>
                                    ユーザ名
                                    <span class="badge badge-danger">必須</span>
                                </label>
                                <input type="text" class="form-control" v-model="user.name" />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label>
                                    ユーザ名（ふりがな）
                                </label>
                                <input type="text" class="form-control" v-model="user.kana" />
                            </div>
                        </div>
                    </div>
                    <div class="mb-3">
                        <label>許可レーティング</label>
                        <div>
                            <label class="btn btn-outline-secondary btn-sm btn-radio" :class="{
                                active: user.rating_id === null,
                            }">
                                <input type="radio" v-model="user.rating_id" :value="null" class="d-none" />
                                <radio :value="user.rating_id === null"></radio>
                                無し
                            </label>
                            <label v-for="v in ratings" :key="v.id" class="btn btn-radio btn-sm"
                                :class="{
                                    active: user.rating_id == v.id,
                                    'btn-outline-warning': v.color == 'warning',
                                    'btn-outline-danger': v.color == 'danger',
                                    'btn-outline-dark': v.color == 'dark',
                                }">
                                <input type="radio" v-model="user.rating_id" :value="v.id" class="d-none" />
                                <radio :value="user.rating_id === v.id"></radio>
                                {{v.rating_name}}
                            </label>
                        </div>
                        <div class="small">
                            以下のレーティングの作品を閲覧できるようにします。生年月日の設定が必要です。
                            R18に設定すると、R15の作品も閲覧できます。<br>
                            <span class="text-danger">
                                ご自身でR18作品を投稿したとき、R15以下をレーティング指定していると、
                                ご自身の作品でもポータル上には表示されません。
                            </span>
                            <div v-for="v in ratings" :key="v.id">
                                <span :class="{'strong': user.rating_id == v.id}">{{v.rating_name}}…{{v.description}}</span>
                            </div>
                        </div>
                    </div>
                    <h3 class="account-caption">
                        本棚の情報
                    </h3>
                    <div class="row">
                        <div class="col-md-7">
                            <div class="mb-3">
                                <label>
                                    サブドメイン
                                    <span class="badge badge-warning">推奨</span>
                                    <span class="badge badge-danger pointer" v-if="org.subdomain && isDomainLock" @click="toggleDomainLock">
                                        <i class="fas fa-lock"></i>
                                    </span>
                                    <span class="badge badge-warning pointer" v-else-if="org.subdomain && !isDomainLock" @click="toggleDomainLock">
                                        <i class="fas fa-lock-open"></i>
                                    </span>
                                </label>
                                <div class="input-group">
                                    <input type="text" class="form-control mono"
                                        :class="{'is-valid': res, 'is-invalid': err}"
                                        v-model="user.subdomain" placeholder="例）example" @input="domainCheck()"
                                        :disabled="!user.email_verified_at"
                                        :readonly="org.subdomain && isDomainLock" />
                                    <div class="input-group-append d-none d-sm-block">
                                        <span class="input-group-text mono text-left">{{$store.state.base_domain}}</span>
                                    </div>
                                </div>
                                <div class="text-success small" v-if="res">{{res}}</div>
                                <div class="text-danger small" v-if="err">{{err}}</div>
                                <div class="text-muted small">
                                    <span class="text-danger" v-if="!org.email_verified_at">メールアドレスを承認してください。<br/></span>
                                    小説を投稿するために必要です。<br/>
                                    32文字以内の半角英数字、ハイフン(-)のみで、一意の文字列を指定します。<br/>
                                    <span class="badge badge-danger" v-if="isDomainLock">サブドメインの変更には制限があります。</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-5">
                            <div class="mb-3" v-if="user.subdomain">
                                <label>本棚の名称</label>
                                <input type="text" class="form-control bookshelf-name" v-model="user.bookshelf_name" placeholder="例）かりぐらしのわたし" />
                                <div class="text-muted small">
                                    投稿小説の本棚につける名称で、サイト名となります。
                                    空欄にすると「貸し本棚」と表記されます。
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mb-3" v-if="user.subdomain">
                        <label>自己紹介</label>
                        <div class="text-pane">
                            <div class="text-pane-dummy">{{user.profile + '\u200b'}}</div>
                            <textarea class="text-pane-editor b-dark"
                                v-model="user.profile"
                                placeholder="例）昭和初期を舞台としたレトロな小説を書いています。"></textarea>
                        </div>
                    </div>
                    <div class="row" v-if="user.subdomain">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label>本棚の隔離</label>
                                <div>
                                    <label class="btn btn-outline-secondary mr-2 mb-0" :class="{active: !user.is_isolated}">
                                        <input type="radio" v-model="user.is_isolated" :value="0" class="d-none">
                                        <radio :value="user.is_isolated === 0"></radio>
                                        隔離しない
                                    </label>
                                    <label class="btn btn-outline-danger mb-0" :class="{active: user.is_isolated}">
                                        <input type="radio" v-model="user.is_isolated" :value="1" class="d-none">
                                        <radio :value="user.is_isolated === 1"></radio>
                                        隔離する
                                    </label>
                                </div>
                                <div class="text-muted small">
                                    本棚からポータルへのすべてのリンクが除去され、完全な独立サイトになります。
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label>一言の最大件数</label>
                                <select class="form-control" v-model="user.news_limit">
                                    <option :value="10">10件</option>
                                    <option :value="20">20件</option>
                                    <option :value="50">50件</option>
                                    <option :value="100">100件</option>
                                    <option :value="200">200件</option>
                                    <option :value="500">500件</option>
                                    <option :value="1000">1,000件</option>
                                </select>
                                <div class="text-muted small">
                                    「一言」の最大件数です。
                                    これを超える一言を投稿すると、古いものから削除されます。<br/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label>本棚に表示するBlueskyアカウント名</label>
                                <div class="input-group">
                                    <div class="input-group-prepend">
                                        <span class="input-group-text">＠</span>
                                    </div>
                                    <input type="text" class="form-control" v-model="user.bluesky" @blur="blueskyAccount()" placeholder="例）example" />
                                    <div class="input-group-append" v-if="user.bluesky">
                                        <a class="input-group-text" :href="`https://bsky.app/profile/${user.bluesky}`" target="_blank">
                                            <i class="fas fa-external-link-alt"></i>
                                        </a>
                                    </div>
                                </div>
                                <div class="text-muted small">
                                    Blueskyアカウントを登録すると、あなたの本棚にリンクが表示されます。
                                    プロフィール画面のURLを貼り付けてください。
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label>本棚に表示するXアカウント名</label>
                                <div class="input-group">
                                    <div class="input-group-prepend">
                                        <span class="input-group-text">＠</span>
                                    </div>
                                    <input type="text" class="form-control" v-model="user.twitter" @blur="xAccount()" placeholder="例）example" />
                                    <div class="input-group-append" v-if="user.twitter">
                                        <a class="input-group-text" :href="`https://x.com/${user.twitter}`" target="_blank">
                                            <i class="fas fa-external-link-alt"></i>
                                        </a>
                                    </div>
                                </div>
                                <div class="text-muted small">
                                    Xアカウントを登録すると、あなたの本棚にリンクが表示されます。
                                    プロフィール画面のURL(例: https://x.com/KatohNoriyasu)を貼り付けてください。
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3" v-if="user.subdomain">
                                <label>Google Anaytics トラッキングID</label>
                                <input type="text" class="form-control" v-model="user.tracking_id" placeholder="例）G-xxxxxxxxx-x" />
                                <div class="text-muted small">
                                    Google Analyticsを登録すると、あなたの本棚へのアクセス解析を行うことができます。
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="mb-3" v-if="user.subdomain">
                                <label>キーワード</label>
                                <input type="text" class="form-control" v-model="user.keywords" placeholder="例）ライトノベル,異世界転生,ローファンタジー,R15" />
                                <div class="text-muted small">
                                    本棚のキーワードです。metaタグに埋め込まれます。半角カンマ区切りで５つ程度指定してください。
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mb-3" v-if="user.subdomain">
                        <label class="caption">作者アイコン</label>
                        <div id="preview" v-if="file.url">
                            <img :src="file.url" class="img-thumbnail cover-img" />
                        </div>
                        <div class="py-2">
                            <div class="btn-group file-selector">
                                <label class="btn btn-secondary m-0">
                                    <i class="fas fa-file-image"></i>
                                    アイコン画像選択...
                                    <input type="file" ref="preview" @change="fileSelected" accept="image/*" class="hide" />
                                </label>
                                <button type="button" @click="removeImage" class="btn btn-light">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        </div>
                        <div class="small">
                            <ul>
                                <li>未設定の場合は何も表示されません。</li>
                                <li>投稿できるサイズは500KBまでです。</li>
                                <li>推奨される画像サイズは幅500px 高さ500pxです。</li>
                                <li>上記アスペクト比以外の画像は、トリミングされます。</li>
                                <li>著作権を侵害する画像、公序良俗に反する画像は指定できません。</li>
                                <li>
                                    <a href="https://icon-z.com/" target="_blank">
                                        顔アイコンジェネレーターZ
                                        <i class="fas fa-external-link-alt"></i>
                                    </a>
                                    や
                                    <a href="https://www.iconpon.com/iconmaker/" target="_blank">
                                        Iconpon
                                        <i class="fas fa-external-link-alt"></i>
                                    </a>
                                    を使うと、ご自身の似顔絵を簡単に作れます。
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="mb-3" v-if="user.subdomain">
                        <label class="caption">本棚バナー</label>
                        <div v-if="banner.url">
                            <img :src="banner.url" class="img-thumbnail banner-img" />
                        </div>
                        <div class="py-2">
                            <div class="btn-group file-selector">
                                <label class="btn btn-secondary m-0">
                                    <i class="fas fa-file-image"></i>
                                    バナー画像選択...
                                    <input type="file" ref="banner" @change="bannerSelected" accept="image/*" class="hide" />
                                </label>
                                <button type="button" @click="removeBanner" class="btn btn-light">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        </div>
                        <div class="small">
                            <ul>
                                <li>未設定の場合は何も表示されません。</li>
                                <li>本棚の上部に表示されるアイキャッチ画像です。</li>
                                <li>投稿できるサイズは3MBまでです。</li>
                                <li>推奨される画像サイズは幅980px以上 高さ350px以上です。</li>
                                <li>表示するブラウザサイズによって、トリミングされます。</li>
                                <li>著作権を侵害する画像、公序良俗に反する画像は指定できません。</li>
                            </ul>
                        </div>
                    </div>
                    <div class="mb-3" v-if="user.subdomain">
                        <label class="caption">リンクバナー</label>
                        <div v-if="link.url">
                            <img :src="link.url" class="img-thumbnail img-link mb-2" />
                            <div class="input-group" v-if="link.url.match(/^http/)">
                                <input type="text" :value="link.url" class="form-control form-control-sm" readonly>
                                <div class="input-group-append">
                                    <button class="btn btn-outline-secondary btn-sm" @click="copy(link.url)">
                                        <i class="far fa-copy"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="py-2">
                            <div class="btn-group file-selector">
                                <label class="btn btn-secondary m-0">
                                    <i class="fas fa-file-image"></i>
                                    リンクバナー画像選択...
                                    <input type="file" ref="link" @change="linkSelected" accept="image/*" class="hide" />
                                </label>
                                <button type="button" @click="removeLink" class="btn btn-light">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        </div>
                        <div class="small">
                            <ul>
                                <li>200 x 40サイズのリンク専用バナーです。</li>
                                <li>画像サイズは幅200px 高さ40pxです。</li>
                                <li>サーチサービスへの登録にご利用ください。</li>
                                <li>著作権を侵害する画像、公序良俗に反する画像は指定できません。</li>
                            </ul>
                        </div>
                    </div>
                    <div class="save">
                        <button type="button" class="btn btn-outline-primary btn-block" @click="save()">
                            <i class="fas fa-save"></i>
                            保存
                        </button>
                    </div>
                    <div class="my-3">
                        <a href="#" @click="load()">
                            すべての情報をもとに戻す
                        </a>
                    </div>
                    <div class="security">
                        <p v-if="hasEmail">
                            <router-link to="/mypage/account/security">
                                パスワード・メールアドレス変更
                            </router-link>
                        </p>
                        <p v-if="!isOAuth">
                            <router-link to="/mypage/account/oauth">
                                メールアドレスを外部サービスと連携する
                            </router-link>
                        </p>
                        <p>
                            <router-link to="/mypage/account/api" class="text-muted">
                                APIトークン
                            </router-link>
                        </p>
                        <p>
                            <router-link to="/mypage/account/resign" class="text-danger">
                                退会する
                            </router-link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.container {
    padding: 0;
}
.btn-radio {
    margin: .5em 1em .5em 0;
}
.strong {
    font-weight: bold;
    color: #494997;
}
.save {
    margin-top: 5em;
}
.line {
    margin: 1rem 0 .5rem;
    padding-bottom: .3rem;
    border-bottom: 1px dashed #ddd;
    &.no-line {
        border-bottom: 0;
    }
}
.security {
    margin: 5rem 0;
    padding: 1rem 0;
    border-top: 1px solid #ddd;
}
.big {
    font-size: 1.3rem;
}
.sideinfo {
    overflow: scroll;
    max-height: calc(100vh - 92px);
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar{
        display:none;
    }
}
.mono {
    font-family: monospace;
    font-size: 1.3rem;
    letter-spacing: 2px;
    text-align: right;
}
.bookshelf-name {
    font-size: 1.3rem;
}
.account-caption {
    margin-top: 1rem;
    font-size: 1.5rem;
    border-bottom: 1px dashed #ddd;
}
.cover-img {
    width: 250px;
    height: 250px;
    object-fit: cover;
}
.banner-img {
    width: 100%;
    height: 250px;
    object-fit: cover;
}
.hide {
    display:none;
}
.badge-token {
    font-size: .9rem;
    padding: .5rem;
}
.text-pane {
    position: relative;
    letter-spacing: 1px;
    border: 0px;
    border-radius: 4px;
    margin: 0 auto .5rem;
    .text-pane-dummy {
        overflow: hidden;
        font-size: 1rem;
        line-height: 2rem;
        box-sizing: border-box;
        padding: .5rem 1rem;
        min-height: 120px;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
        letter-spacing: 1px;
        border: 0;
        border-radius: 4px;
        opacity: 0;
    }
    .text-pane-editor {
        position: absolute;
        top: 0;
        left: 0;
        font-size: 1rem;
        line-height: 2rem;
        letter-spacing: 1px;
        display: block;
        overflow: hidden;
        box-sizing: border-box;
        padding: .5rem 1rem;
        width: 100%;
        height: 100%;
        border: 0;
        resize: none;
        &:focus {
            outline: none;
        }
        border-radius: 4px;
    }
}
.btn-icon
{
    img {
        width: 16px;
        height: 16px;
    }
}
@media screen and (max-width: 575px) {
    .mono {
        text-align: left;
    }
}
</style>

<script>
import radio from "components/Radio";
export default {
    components: {
        radio
    },
    data() {
        return {
            user: {},
            org: {},
            isLoading: false,
            defaultImage: "/img/mypage/default-user.png",
            timeoutID: null,
            res: null,
            err: null,
            // プロファイル
            file: {
                info: null,
                url: null,
            },
            // バナー
            banner: {
                info: null,
                url: null,
            },
            // リンクバナー
            link: {
                info: null,
                url: null,
            },
            isDomainLock: true,
            disableDates: {
                customPredictor(date) {
                    if (date.getFullYear() < 1900 || moment(date).isSameOrAfter(moment())) {
                        return true;
                    }
                }
            },
            ratings: [],
        }
    },
    methods: {
        load(login = false) {
            this.isLoading = true;
            axios.get("/mypage/user/get").then(res => {
                this.user = res.data;
                Object.assign(this.org, this.user);
                if (this.user.profile_url) {
                    this.file.url = this.user.profile_url + '?' + this.user.profile_hash;
                }
                if (this.user.banner_url) {
                    this.banner.url = this.user.banner_url + '?' + this.user.banner_hash;
                }
                if (this.user.banner) {
                    this.link.url = this.user.banner.picture.url + '?' + this.user.banner.picture.hash;
                }
                if (login) {
                    this.$store.commit("login", this.user);
                    this.$observer.dispatch("login");
                }
                setTimeout(() => {$('[data-toggle="tooltip"]').tooltip()});
            }).catch(err => {
                alert.err(err);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        save() {
            this.isLoading = true;
            axios.post("/mypage/user/save", this.user).then(res => {
                // 画像アップロード（空は削除）
                const fd = new FormData();
                fd.append('profile', this.file.info);
                fd.append('profile_url', this.file.url);
                fd.append('banner', this.banner.info);
                fd.append('banner_url', this.banner.url);
                fd.append('link', this.link.info);
                fd.append('link_url', this.link.url);
                axios.post("/mypage/user/upload", fd).then(res => {
                    alert.msg("ユーザ情報を保存しました。");
                    this.load(true);
                }).catch(err => {
                    alert.err(err.response);
                    this.isLoading = false;
                }).finally(() => {
                    this.isLoading = false;
                });
            }).catch(err => {
                alert.err(err.response);
                this.isLoading = false;
            }).finally(() => {
                this.isLoading = false;
            });
        },
        domainCheck() {
            if (this.timeoutID) {
                clearTimeout(this.timeoutID);
            }
            this.timeoutID = setTimeout(() => {
                axios.get("/mypage/user/domain-check", {params: {subdomain: this.user.subdomain}}).then(res => {
                    this.res = res.data;
                    this.err = null;
                }).catch(err => {
                    this.err = err.response.data;
                    this.res = null;
                });
                clearTimeout(this.timeoutID);
            }, 1000);
        },
        toggleDomainLock() {
            if (this.isDomainLock) {
                Swal.fire({
                    title: "サブドメイン変更",
                    html: `サブドメインの変更ロックを解除します。
                    <ul class="small text-left my-3">
                        <li>サブドメインは 5 回まで変更することができます。</li>
                        <li>旧サブドメインから新サブドメインへリダイレクトします。</li>
                        <li>古いサブドメインに戻すこともできます。</li>
                        <li>サブドメインを放棄することはできません。</li>
                    </ul>
                    よろしいですか？`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "ロック解除",
                    cancelButtonText: "キャンセル",
                    confirmButtonColor: "crimson",
                    cancelButtonColor: "gray",
                    reverseButtons: true,
                }).then(confirm => {
                    if (confirm.isConfirmed) {
                        this.isDomainLock = false;
                    }
                });
            } else {
                this.isDomainLock = true;
            }
        },
        fileSelected() {
            if (this.$refs.preview.files[0].size > 500 * 1024) {
                alert.err("ファイルサイズが大きすぎます: " + this.getSize(this.$refs.preview.files[0].size));
                return;
            }
            this.file.info = this.$refs.preview.files[0];
            this.file.url = URL.createObjectURL(this.file.info);
        },
        bannerSelected() {
            if (this.$refs.banner.files[0].size > 3000 * 1024) {
                alert.err("ファイルサイズが大きすぎます: " + this.getSize(this.$refs.banner.files[0].size));
                return;
            }
            this.banner.info = this.$refs.banner.files[0];
            this.banner.url = URL.createObjectURL(this.banner.info);
        },
        linkSelected() {
            if (this.$refs.link.files[0].size > 500 * 1024) {
                alert.err("ファイルサイズが大きすぎます: " + this.getSize(this.$refs.link.files[0].size));
                return;
            }
            this.link.info = this.$refs.link.files[0];
            this.link.url = URL.createObjectURL(this.link.info);
        },
        removeImage() {
            this.file.info = null;
            this.file.url = "";
        },
        removeBanner() {
            this.banner.info = null;
            this.banner.url = "";
        },
        removeLink() {
            this.link.info = null;
            this.link.url = "";
        },
        getSize(filesize) {
            if (filesize >= 1073741824) {
                return Math.round(filesize / 1073741824 * 100) / 100 + "GB";
            } else if (filesize >= 1048576) {
                return Math.round(filesize / 1048576 * 100) / 100 + "MB";
            } else if (filesize >= 1024) {
                return Math.round(filesize / 1024 * 100) / 100 + "KB";
            }
            return filesize + "B";
        },
        init() {
            axios.get("/master/ratings").then(res => {
                this.ratings = res.data;
            }).catch(err => {
                alert.err(err.response);
            });
        },
        copy(url) {
            navigator.clipboard.writeText(url).then(() => {
                alert.msg("URLをコピーしました。");
            }).catch(err => { alert.err(err) });
        },
        blueskyAccount() {
            const account = this.user.bluesky.match(/bsky\.app\/profile\/(.+)/);
            if (account) {
                this.user.bluesky = account[1];
            }
        },
        xAccount() {
            const account = this.user.twitter.match(/x\.com\/([a-zA-Z]+)/);
            if (account) {
                this.user.twitter = account[1];
            }
        }
    },
    computed: {
        isOAuth() {
            return this.user.is_google;
        },
        hasEmail() {
            if (this.user && this.user.email) {
                return this.user.email.match(/@/);
            }
            return false;
        }
    },
    mounted() {
        this.init();
        this.load();
        setTimeout(() => {
            this.useHead({
                title: "アカウント | 貸し本棚",
                link: [ { rel: "canonical", href: location.origin + location.pathname, id: "canonical" } ],
                meta: [
                    { name: "keywords", content: "貸し本棚,小説投稿,Web小説,小説展示,オンライン小説,縦書き小説,置き場,文芸,ライトノベル", id: "meta-keywords" },
                    { name: "description", content: "小説を書くこと、読むことにフォーカスした小説投稿サイトです。作家さん独自のサブドメインで、ご自身の小説を公開できます。横断的に編集ができるエディタで原稿執筆することも、過去の作品をレーティングを気にせず展示することもできます。", id: "meta-description" }
                ]
            });
        }, 1000);
    }
}
</script>